import React, { useCallback } from "react";

import { MapDisplay as MapDisplayComponent } from "heat-next";
import { MapContainer as MapContainerComponent } from "heat-next";

import { useFeatureToggles } from "@/context/feature-toggles";
import { useModal } from "@/context/modal/modal";
import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";

import type { MapMovementData } from "@/types/session-details";

import { Features } from "@/enums/features";

interface MapDisplayProps {
    subDimensionData?: any;
}

const MapDisplay: React.FC<MapDisplayProps> = ({ subDimensionData }) => {
    const { isFeatureActive } = useFeatureToggles();
    const isSymbolToggleActive = isFeatureActive(Features.SymbolToggle);

    const { isFetchingMapData, isErrorMapData, mapTimelineData } = useSessionDetailsContext();
    const { openModal, setWidth, setHeight } = useModal();

    const handleExpandContainerButtonClick = useCallback(() => {
        setWidth("77.25682295%"); //for 1104px modal width, in breakpoint 1440px
        setHeight("87.4%");
        openModal({
            isExpandableContent: true,
            expandableContentTitle: "Map",
            content: (
                <MapContainerComponent
                    data={subDimensionData ?? mapTimelineData}
                    openInModal={true}
                    isSymbolToggleActive={isSymbolToggleActive}
                />
            ),
        });
    }, [subDimensionData, mapTimelineData]);

    const hasMovementData = mapTimelineData?.movements?.every(
        (item: MapMovementData) => item.movement.length > 0,
    );

    const isEmptyState =
        !isErrorMapData &&
        !subDimensionData &&
        (mapTimelineData === null ||
            mapTimelineData?.dataset?.length === 0 ||
            !hasMovementData ||
            Object.keys(mapTimelineData).length === 0);

    if (isFetchingMapData) {
        return <ContentLoader height="31.813125rem" />;
    }

    if (isErrorMapData && !isFetchingMapData) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    description="error-state:description"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="28.813125rem"
                />
            </ComponentWrapper>
        );
    }

    if (isEmptyState) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:map-display:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="28.813125rem"
                />
            </ComponentWrapper>
        );
    }

    return (
        <MapDisplayComponent
            data={subDimensionData ?? mapTimelineData}
            handleExpandContainerButtonClick={handleExpandContainerButtonClick}
            isSymbolToggleActive={isSymbolToggleActive}
        />
    );
};

export default MapDisplay;
